/**
 * RelatedTools — internal-linking strip for sister tools.
 *
 * Why this exists: Google's PageRank flows along internal links. Without
 * cross-links, our public tool pages (Water Budget, Catch Cup, BYOS,
 * Fungicide Cheat Sheet, Rebate Helper) sit as orphaned islands and rank
 * weakly. This component injects a 3–5 link footer on every public tool
 * page so traffic earned by one tool reinforces the rest.
 *
 * Drop into a public page like:
 *   <RelatedTools currentPath="/water-budget" />
 * It auto-excludes the current page from its own list.
 */
import React from 'react';
import { Link } from 'react-router-dom';

const ALL_TOOLS = [
  { path: '/water-budget',           name: 'Water Budget',           tag: 'ET₀ runtime calculator' },
  { path: '/catch-cup',              name: 'Catch Cup Audit',        tag: 'measure your sprinkler DU' },
  { path: '/byos',                   name: 'BYOS — Bring Your Own Station', tag: 'connect your PWS' },
  { path: '/fungicide-cheat-sheet',  name: 'Fungicide Cheat Sheet',  tag: 'Smith-Kerns + FRAC rotation' },
  { path: '/rachio/rebate-helper',   name: 'Rebate Helper',          tag: 'Round Rock smart-irrigation rebate' },
  { path: '/rachio/install-checklist', name: 'Rachio Install Checklist', tag: 'install-day prep' },
  { path: '/try-diagnosis',          name: 'AI Photo Diagnosis',     tag: 'snap a leaf, get an ID' },
  { path: '/kit',                    name: 'TurfPulse Lab Kit',      tag: 'curated soil-test kit' },
];

export const RelatedTools = ({ currentPath, max = 4, heading = 'Related TurfPulse tools' }) => {
  const items = ALL_TOOLS.filter((t) => t.path !== currentPath).slice(0, max);
  if (items.length === 0) return null;
  return (
    <section
      className="mt-12 pt-8 border-t border-border"
      aria-labelledby="related-tools-heading"
      data-testid="related-tools"
    >
      <h2
        id="related-tools-heading"
        className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-semibold mb-4"
      >
        {heading}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map((t) => (
          <Link
            key={t.path}
            to={t.path}
            className="block rounded-lg border border-border bg-background hover:bg-muted/50 px-4 py-3 transition-colors"
            data-testid={`related-tool-${t.path.replace(/\W+/g, '-')}`}
          >
            <div className="font-semibold text-sm text-foreground">{t.name} →</div>
            <div className="text-xs text-muted-foreground mt-0.5">{t.tag}</div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default RelatedTools;
